import { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FileCheck2, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { platformEnsureIngresoSimplificado } from "@/services/ingresoSimplificadoService";
import type { TenantOnboardingOverview } from "@/services/platformTenantsService";

interface Props {
  tenantId: string;
  overview: TenantOnboardingOverview;
}

/**
 * Root-only entry point to start a homologación simplificada for ANY tenant —
 * including regularization emisores that never self-registered one. Creates the
 * ingreso_simplificado request via the DGI-free ensure-request endpoint, then
 * drops the operator into the /homologaciones driver (where postulación etc.
 * are explicit clicks). Gated on the tenant having a CONFIRMED DGI e-Factura
 * user, since postulación requires it — without it the driver is a dead end.
 */
export function TenantHomologacionCard({
  tenantId,
  overview,
}: Props): JSX.Element | null {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);

  if (!overview.hasDgiPortalUser || !overview.dgiPortalUserConfirmed) {
    return null;
  }

  const handleStart = async (): Promise<void> => {
    setStarting(true);
    try {
      await platformEnsureIngresoSimplificado(tenantId);
      navigate(`/homologaciones/${tenantId}`);
    } catch {
      toast.error("No se pudo iniciar la homologación. Intentá de nuevo.");
      setStarting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileCheck2 className="h-4 w-4" />
          Homologación simplificada
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Iniciá el alta de emisor electrónico (homologación simplificada) para
          esta empresa. Después la gestionás desde la cola de Homologaciones.
        </p>
        <Button onClick={handleStart} disabled={starting}>
          {starting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Iniciar homologación simplificada
        </Button>
      </CardContent>
    </Card>
  );
}
